import { type ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import type { UserProfile } from '@molecule/app-auth'
import {
  useAuth,
  useChangePassword,
  useDevice,
  useHttpClient,
  useI18nError,
  usePush,
  useStoragePreference,
  useTranslation,
  useVersion,
} from '@molecule/app-react'
import {
  disablePushOnCurrentDevice,
  enablePushOnCurrentDevice,
  type PushToggleFailureReason,
  readCurrentDevicePushEnabled,
} from '@molecule/app-settings-panel-react'
import { getClassMap } from '@molecule/app-ui'
import { Alert, Button, Flex, Icon, Input, Modal, Switch } from '@molecule/app-ui-react'

import { silentErrors } from '../bonds/app-http.js'
import { useIsMobile } from '../hooks/useMediaQuery.js'
import { serverErrorMessage } from '../lib/server-error.js'
import {
  AiUsageMeter,
  BrokerUsage,
  ExtraUsageControls,
  ManageBillingButton,
  OverLimitBanner,
  PlanSummary,
  ResourceUsageBars,
  useAccountUsage,
} from '../pages/Billing.js'
import { ensurePushServiceWorker } from '../pwa.js'
import { isGuestUser } from '../utilities/guestClaim.js'
import { AccountAiSection } from './AccountAiSection.js'
import { AccountApiTokensSection } from './AccountApiTokensSection.js'
import { AccountBadgeSection } from './AccountBadgeSection.js'
import { AccountSandboxSection } from './AccountSandboxSection.js'
import { AccountSessionSharingSection } from './AccountSessionSharingSection.js'
import { AccountTeamSection } from './AccountTeamSection.js'
import { Footer } from './Footer.js'
import { MoleculeSpinner } from './MoleculeSpinner.js'
import { ProfileEditor, SavedIndicator } from './ProfileEditor.js'
import { UpgradeModal, type UpgradeModalView } from './UpgradeModal.js'

/**
 * Collapsible-section bookkeeping. Only Profile and Account (the "here to
 * change something" cards) start open; everything else collapses to a header
 * + summary row so the drawer is a scannable index instead of one long scroll.
 * Open states persist per browser so the drawer stops resetting on every open.
 */
const SECTIONS = [
  'profile',
  'account',
  'ai',
  'privacy',
  'billing',
  'team',
  'usage',
  'devices',
  'apiTokens',
] as const
type SectionId = (typeof SECTIONS)[number]

const SECTIONS_DEFAULT_OPEN: Record<SectionId, boolean> = {
  profile: true,
  account: true,
  ai: false,
  privacy: false,
  billing: false,
  team: false,
  usage: false,
  devices: false,
  apiTokens: false,
}

const SECTIONS_STORAGE_KEY = 'mol:settings-sections:v1'
/** Set `localStorage['mol:settings-nav']='1'` (then reload) to preview the (#4) anchor-chip nav. */
const NAV_FLAG_KEY = 'mol:settings-nav'

/** English fallback labels for the #4 nav chips (i18n keys take precedence). */
const SECTION_LABELS: Record<SectionId, string> = {
  profile: 'Profile',
  account: 'Account',
  ai: 'AI',
  privacy: 'Privacy',
  billing: 'Billing',
  team: 'Team',
  usage: 'Usage',
  devices: 'Devices',
  apiTokens: 'API tokens',
}

/**
 * Keeps only the known section ids with boolean values.
 *
 * The persisted value comes from the bonded provider, where an absent, corrupt
 * or foreign entry reads as `null` and a partially-written one can carry
 * anything: everything unknown is dropped so the defaults apply.
 * @param parsed - The stored value.
 * @returns The section states that are safe to apply.
 */
function sanitizeSectionState(parsed: unknown): Partial<Record<SectionId, boolean>> {
  if (!parsed || typeof parsed !== 'object') return {}
  const out: Partial<Record<SectionId, boolean>> = {}
  for (const id of SECTIONS) {
    const value = (parsed as Record<string, unknown>)[id]
    if (typeof value === 'boolean') out[id] = value
  }
  return out
}

/**
 * The /billing-style card chrome every panel section shares: bordered rounded
 * box, a larger heading (with an optional right-side action), then content.
 *
 * Collapsible mode turns the heading into the expand/collapse toggle; while
 * collapsed the optional `summary` renders as its OWN muted line under the
 * header — a block, never squeezed inline beside the title (the same
 * text-inline-with-control squish this panel keeps having to fix). `pinned`
 * renders between that summary and the collapsible content in BOTH states —
 * the Billing card keeps its upgrade CTA there, so the header stays clean and
 * the CTA sits under the plan line without ever hiding when the card
 * collapses. The `action` node (a spinner) deliberately sits OUTSIDE the
 * toggle button: nested buttons are invalid HTML and the CTA must stay
 * clickable when the card is collapsed.
 */
function SectionCard({
  title,
  action,
  molId,
  children,
  collapsible = false,
  summary,
  pinned,
  open = true,
  onToggle,
}: {
  title?: string
  action?: React.ReactNode
  molId?: string
  children: React.ReactNode
  /** Render the heading as an expand/collapse toggle. */
  collapsible?: boolean
  /** One-line state summary shown under the header while collapsed. */
  summary?: ReactNode
  /** Always-visible node between the summary and the collapsible content. */
  pinned?: ReactNode
  /** Controlled expanded state (collapsible mode). */
  open?: boolean
  /** Toggle callback (collapsible mode). */
  onToggle?: (next: boolean) => void
}) {
  const cm = getClassMap()
  const contentId = useId()
  const heading = title ? (
    <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{title}</h3>
  ) : null
  const headerRow =
    title || action ? (
      <Flex align="center" justify="between" gap="sm" className={cm.sp('mb', 3)}>
        {collapsible ? (
          // mol-bespoke-button: the whole header row is the toggle — a flat
          // hit area carrying the title, not a pill CTA; the CVA button
          // recipes would chrome it as one. Row chrome + the hover/focus fill
          // live in .mol-section-toggle (index.css).
          <button
            type="button"
            className="mol-section-toggle"
            data-mol-id={molId ? `${molId}-toggle` : undefined}
            onClick={() => onToggle?.(!open)}
            aria-expanded={open}
            aria-controls={contentId}
          >
            {heading}
            <Icon
              name="chevron-down"
              size={16}
              aria-hidden="true"
              style={{
                marginLeft: 'auto',
                flexShrink: 0,
                transition: 'transform 0.15s ease',
                transform: open ? 'none' : 'rotate(-90deg)',
              }}
            />
          </button>
        ) : (
          heading
        )}
        {action}
      </Flex>
    ) : null
  return (
    <section
      data-mol-id={molId}
      style={{ border: '1px solid var(--color-border)', borderRadius: 12, padding: 16 }}
    >
      {headerRow}
      {/* The summary line — full width under the header, only while collapsed. */}
      {collapsible && !open && summary ? (
        <div
          className={cm.cn(cm.textSize('sm'), cm.textMuted)}
          style={{ marginTop: -4, marginBottom: pinned ? 12 : undefined }}
        >
          {summary}
        </div>
      ) : null}
      {pinned ? <div style={{ marginBottom: 12 }}>{pinned}</div> : null}
      {/* Collapsed content is hidden, not unmounted, HERE — the parent decides
          the first mount (see everOpenedSection) so a never-opened card fires
          no reads. Keeping mounted afterwards preserves in-progress edits. */}
      <div
        id={contentId}
        role={collapsible ? 'region' : undefined}
        aria-hidden={!open}
        style={open ? undefined : { display: 'none' }}
      >
        {children}
      </div>
    </section>
  )
}

/** Sub-heading inside a card (the /billing card-title treatment). */
function CardSubheading({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{children}</div>
}

interface Device {
  id: string
  name: string
  platform: string
  lastSeen?: string
  /** Whether this row is the session reading the list (annotated by the API). */
  current?: boolean
}

/** The delete-account dialog's copy (also the en locale value). */
const DELETE_ACCOUNT_WARNING =
  'This permanently deletes your projects, deployed apps, databases and subscription. Teammates lose access to projects you shared with them. Enter your password to confirm.'

const DELETE_ACCOUNT_WARNING_OAUTH =
  'This permanently deletes your projects, deployed apps, databases and subscription. Teammates lose access to projects you shared with them. Confirm to continue.'

/** HTTP status of a thrown request error, whatever transport shape it has. */
function httpStatusOf(error: unknown): number | undefined {
  const e = error as { status?: unknown; response?: { status?: unknown } } | null | undefined
  if (typeof e?.status === 'number') return e.status
  const responseStatus = e?.response?.status
  return typeof responseStatus === 'number' ? responseStatus : undefined
}

/**
 * The machine-readable `errorKey` the API puts in an error body, if any.
 *
 * @param error - The thrown request error.
 * @returns The key, or undefined when the body carries none.
 */
function errorKeyOf(error: unknown): string | undefined {
  return errorBodyString(error, 'errorKey')
}

/**
 * One string field of an API error body (`errorKey`, `reason`, `error`), if any.
 *
 * @param error - The thrown request error.
 * @param field - The body field to read.
 * @returns The non-empty string, or undefined.
 */
function errorBodyString(error: unknown, field: string): string | undefined {
  const e = error as {
    data?: Record<string, unknown>
    response?: { data?: Record<string, unknown> }
  } | null
  const value = e?.response?.data?.[field] ?? e?.data?.[field]
  return typeof value === 'string' && value ? value : undefined
}

/**
 * Plain copy for a failed `DELETE /users/:id`. Chooses by the body's `errorKey`
 * (wrong or missing password, busy), falling back to status when there is none:
 * the server answers 403 when the confirming password is wrong and 503 when a
 * step must be retried (nothing is deleted then). A 503 is not always the
 * subscription: a project created while the deletion ran answers 503 too
 * (`projects-remain`), so a busy answer shows the server's own sentence and
 * only falls back to the subscription line when the body carries none.
 * Anything else is a generic retry line, never the transport's raw message.
 *
 * @param error - The thrown request error.
 * @param t - The translate function.
 * @returns The sentence to show in the dialog.
 */
export function deleteAccountErrorMessage(
  error: unknown,
  t: (key: string, values: undefined, options: { defaultValue: string }) => string,
): string {
  const status = httpStatusOf(error)
  const key = errorKeyOf(error)
  if (key === 'user.error.passwordRequired') {
    return t('settings.deleteAccountModal.passwordRequired', undefined, {
      defaultValue: 'Enter your password to delete your account.',
    })
  }
  if (key === 'user.error.passwordMismatch' || (!key && status === 403)) {
    return t('settings.deleteAccountModal.wrongPassword', undefined, {
      defaultValue: 'That password is not right.',
    })
  }
  if (key === 'projects-remain' || errorBodyString(error, 'reason') === 'projects-remain') {
    return t('settings.deleteAccountModal.projectsRemain', undefined, {
      defaultValue: 'One of your projects could not be removed yet. Try again in a minute.',
    })
  }
  if (key === 'service.busy' || (!key && status === 503)) {
    const serverSentence = errorBodyString(error, 'error')
    if (serverSentence) return serverSentence
    return t('settings.deleteAccountModal.subscriptionFailed', undefined, {
      defaultValue:
        'We could not cancel your subscription, so nothing was deleted. Try again in a minute.',
    })
  }
  return t('settings.failedToDeleteAccount', undefined, {
    defaultValue: 'Could not delete your account. Try again in a minute.',
  })
}

/**
 * File name for a data export saved on `date` (local calendar day).
 *
 * @param date - When the export was saved.
 * @returns `molecule-export-YYYY-MM-DD.json`.
 */
export function dataExportFileName(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `molecule-export-${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}.json`
}

/**
 * Hand a blob to the browser as a download.
 *
 * @param blob - The file contents.
 * @param fileName - The name to save it under.
 */
function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Revoking right after click() cancels large downloads in Safari/Firefox.
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

export function SettingsPanel({ onClose }: { onClose: () => void }) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { user, logout, setUser } = useAuth<UserProfile>()
  const http = useHttpClient()
  useVersion()
  const { requestPermission, register: registerPush, unregister: unregisterPush } = usePush()
  const { deviceInfo } = useDevice()
  const { status: pwStatus, error: pwError, changePassword, reset: resetPw } = useChangePassword()
  const pwErrorMessage = useI18nError(pwError)
  // Plan / extra usage / usage meters — the former /billing page's data,
  // now rendered in the Billing + Usage sections below.
  const { usage, loading: usageLoading, error: usageError, reload: reloadUsage } = useAccountUsage()

  // Email editing — explicit flow: the address only changes via the "Update
  // email" button, which re-authenticates (current password) for password
  // accounts. Never saved on blur: silently swapping the login identifier +
  // recovery anchor while tabbing through a form was the old (unsafe) UX.
  const userEmail = user?.email || ''
  const [email, setEmail] = useState(userEmail)
  useEffect(() => setEmail(userEmail), [userEmail])
  const [emailSaving, setEmailSaving] = useState(false)
  const [emailError, setEmailError] = useState('')
  // Set (to the new address) right after a successful change — drives the
  // "verification sent" notice.
  const [emailChangedTo, setEmailChangedTo] = useState('')
  // Password-confirm step for the change (password accounts only).
  const [showEmailConfirmModal, setShowEmailConfirmModal] = useState(false)
  const [emailConfirmPassword, setEmailConfirmPassword] = useState('')
  // OAuth accounts have no password to present — their session is the credential.
  const isOAuthAccount = !!(user as { oauthServer?: string } | null)?.oauthServer
  // molecule-dev's verification truth is emailConfirmedAt (the AI gate reads it).
  const emailVerified = !!(user as { emailConfirmedAt?: string | null } | null)?.emailConfirmedAt
  const emailDirty = email.trim() !== '' && email.trim() !== userEmail
  // Resend-verification state
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'throttled'>('idle')

  // Password change
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')

  // Devices
  const [devices, setDevices] = useState<Device[]>([])
  const [devicesLoading, setDevicesLoading] = useState(true)
  // Device revocation. `DELETE /devices/:id` has always existed and deletes the
  // row the JWT is bound to, which ends that session for every copy of the
  // token — but nothing in the product called it, so a lost phone stayed signed
  // in and the panel listed it as an inert row.
  const [revokingDevice, setRevokingDevice] = useState<Device | null>(null)
  const [revokeBusy, setRevokeBusy] = useState(false)
  const [deviceError, setDeviceError] = useState('')

  // Plan — derived from the signed-in user's planKey (it used to be a
  // hardcoded 'Free' that never changed after an upgrade). Mirrors the API's
  // planKeyCategory: the key's BASE (before `+`-suffixes like seats/usage
  // packs) decides, and every paid base counts — stripe (web checkout), team,
  // and the apple/google in-app purchases, so an IAP subscriber is not shown
  // "Upgrade to hide".
  const planKey = (user as { planKey?: string } | null)?.planKey
  const basePlanKey = (planKey ?? '').split('+')[0]
  const isPaidPlan = /^(stripe|team|apple|google)/.test(basePlanKey)
  const plan = basePlanKey.startsWith('team') ? 'Team' : isPaidPlan ? 'Pro' : 'Free'
  // The upgrade flow opens IN PLACE over the panel (the workspace's intercepted
  // modal isn't mounted on dashboard-side pages, so an anchor navigated away).
  const [upgradeView, setUpgradeView] = useState<UpgradeModalView | null>(null)
  // Profile auto-save confirmation — shown beside the Profile card heading.
  const [profileSaved, setProfileSaved] = useState(false)

  // Delete account
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletePassword, setDeletePassword] = useState('')
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  // Data export — the in-app half of the privacy policy's export promise
  // (`GET /users/:id/data-export`; the CLI half is `mlcl account export`).
  const [exportBusy, setExportBusy] = useState(false)
  const [exportError, setExportError] = useState(false)

  // Push notifications — the toggle reflects SERVER truth (does THIS device have
  // a stored subscription the api can actually send to), not just browser
  // permission. The full enable chain (permission → runtime VAPID key →
  // subscribe → persist on the device row) lives in the shared
  // @molecule/app-settings-panel-react helpers so this panel can't silently drop
  // a step and diverge from it again (the old hand-rolled toggle never fetched
  // the VAPID key nor persisted the subscription, so no push ever arrived).
  const [pushEnabled, setPushEnabled] = useState(false)
  const [pushBusy, setPushBusy] = useState(false)
  const [pushError, setPushError] = useState<PushToggleFailureReason | null>(null)
  useEffect(() => {
    let cancelled = false
    readCurrentDevicePushEnabled(http).then((enabled) => {
      if (!cancelled) setPushEnabled(enabled)
    })
    return () => {
      cancelled = true
    }
  }, [http])

  // Collapsible sections — controlled here so the nav chips (preview flag)
  // can expand + scroll to a card, and so open states persist across opens.
  // The value comes from the bonded provider as a SUBSCRIPTION, not a one-shot
  // render read: an asynchronous provider's hydration therefore opens the
  // sections the user left open instead of permanently showing the defaults.
  const {
    value: storedSectionState,
    ready: sectionStateReady,
    setValue: persistSectionState,
  } = useStoragePreference<Record<string, unknown>>(SECTIONS_STORAGE_KEY)
  const openSections = useMemo(
    () => ({ ...SECTIONS_DEFAULT_OPEN, ...sanitizeSectionState(storedSectionState) }),
    [storedSectionState],
  )
  const openSectionsRef = useRef(openSections)
  openSectionsRef.current = openSections
  // A section mounts its content on first expansion and stays mounted after
  // (hidden when re-collapsed) — a never-opened card fires none of its reads.
  const [everOpened, setEverOpened] = useState<Partial<Record<SectionId, boolean>>>(() => {
    const initial = Object.entries({
      ...SECTIONS_DEFAULT_OPEN,
      ...sanitizeSectionState(storedSectionState),
    }).filter(([, isOpen]) => isOpen)
    return Object.fromEntries(initial) as Partial<Record<SectionId, boolean>>
  })
  // Hydration (or another tab) can open a section after the first render: mount
  // its content then, the same as a user expansion would.
  useEffect(() => {
    if (!sectionStateReady) return
    setEverOpened((prev) => {
      const next = { ...prev }
      let changed = false
      for (const id of SECTIONS) {
        if (openSections[id] && !next[id]) {
          next[id] = true
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [sectionStateReady, openSections])
  const setSectionOpen = useCallback(
    (id: SectionId, next: boolean): void => {
      if (openSectionsRef.current[id] === next) return
      persistSectionState({ ...openSectionsRef.current, [id]: next })
      if (next) setEverOpened((prev) => (prev[id] ? prev : { ...prev, [id]: true }))
    },
    [persistSectionState],
  )
  const sectionRefs = useRef<Partial<Record<SectionId, HTMLElement | null>>>({})
  // #4 preview (nav chips): flip on by storing `mol:settings-nav` = 1 in the
  // console (the provider JSON-encodes it, so a raw `1` parses to the number 1).
  const { value: navFlag } = useStoragePreference<unknown>(NAV_FLAG_KEY)
  // Anything unreadable reads as absent: the preview flag just stays off.
  const navEnabled = navFlag === 1 || navFlag === '1'
  const expandAndReveal = useCallback(
    (id: SectionId) => {
      setSectionOpen(id, true)
      // The content mounts this render; reveal after it paints. rAF is not
      // guaranteed in every environment — fall back to a timeout.
      const reveal = (): void => {
        sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(reveal)
      } else {
        setTimeout(reveal, 0)
      }
    },
    [setSectionOpen],
  )

  const loadDevices = useCallback(async () => {
    try {
      // `GET /devices` is a resource-infrastructure route: the body is the
      // FLAT device array, not a { data } envelope. Reading .data.data made
      // this list render empty forever.
      const response = await http.get<Device[]>('/devices')
      setDevices(Array.isArray(response.data) ? response.data : [])
    } catch (error) {
      console.error('Failed to load devices', error)
    } finally {
      setDevicesLoading(false)
    }
  }, [http])

  // Lazy: the Devices card fires its read only once someone opens it —
  // a panel open that never touches devices no longer pays for the list.
  useEffect(() => {
    if (everOpened.devices) void loadDevices()
  }, [everOpened.devices, loadDevices])

  const confirmRevokeDevice = useCallback(async () => {
    if (!revokingDevice) return
    setRevokeBusy(true)
    setDeviceError('')
    try {
      await http.delete(`/devices/${revokingDevice.id}`)
      const wasCurrent = revokingDevice.current === true
      setRevokingDevice(null)
      if (wasCurrent) {
        // Revoking the session you are holding: end it here too, rather than
        // leaving a UI that 401s on its next request with no explanation.
        await logout()
        navigate('/login')
        return
      }
      await loadDevices()
    } catch (_error) {
      setDeviceError(
        t('settings.devices.revokeFailed', undefined, {
          defaultValue: 'Could not sign that device out.',
        }),
      )
    } finally {
      setRevokeBusy(false)
    }
  }, [http, revokingDevice, loadDevices, logout, navigate, t])

  /** The Update-email entry point: password accounts confirm first. */
  const handleUpdateEmailClick = () => {
    setEmailError('')
    if (isOAuthAccount) {
      void submitEmailChange()
    } else {
      setEmailConfirmPassword('')
      setShowEmailConfirmModal(true)
    }
  }

  const submitEmailChange = async (currentPasswordValue?: string) => {
    const nextEmail = email.trim()
    if (!nextEmail || !user) return
    setEmailSaving(true)
    setEmailError('')
    setEmailChangedTo('')
    try {
      const res = await http.patch<{ props?: { email?: string } }>(`/users/${user.id}`, {
        email: nextEmail,
        ...(currentPasswordValue ? { currentPassword: currentPasswordValue } : {}),
      })
      const storedEmail = res.data?.props?.email ?? nextEmail
      // Reflect locally: new address, no longer verified (the server just
      // un-confirmed it and emailed a verification link to the new address).
      setUser({
        ...user,
        email: storedEmail,
        emailConfirmedAt: null,
        emailVerified: false,
      } as UserProfile)
      setEmail(storedEmail)
      setEmailChangedTo(storedEmail)
      setResendState('idle')
      setShowEmailConfirmModal(false)
      setEmailConfirmPassword('')
    } catch (err) {
      setEmailError(
        serverErrorMessage(err) ??
          (err instanceof Error ? err.message : t('settings.failedToUpdateEmail')),
      )
    } finally {
      setEmailSaving(false)
    }
  }

  const handleResendVerification = async () => {
    setResendState('sending')
    try {
      await http.post('/users/resend-verification', {})
      setResendState('sent')
    } catch (err) {
      const status = (err as { status?: number })?.status
      setResendState(status === 429 ? 'throttled' : 'idle')
      if (status !== 429) {
        console.error('Failed to resend verification email', err)
      }
    }
  }

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await changePassword(currentPassword, newPassword)
      setShowPasswordModal(false)
      setCurrentPassword('')
      setNewPassword('')
      resetPw()
    } catch (_error) {
      // Error is surfaced through pwError hook state; no additional handling needed
    }
  }

  const handleTogglePush = async (enabled: boolean) => {
    if (pushBusy) return
    setPushBusy(true)
    setPushError(null)
    try {
      // molecule.dev registers the service worker lazily (only inside the IDE),
      // but this toggle is reachable from account settings with no project open
      // — so ensure a SW is active before subscribing, or register() throws
      // "No service worker is registered" and enabling fails every time.
      if (enabled && !(await ensurePushServiceWorker())) {
        setPushEnabled(false)
        setPushError('register-failed')
        return
      }
      const result = enabled
        ? await enablePushOnCurrentDevice({ http, requestPermission, register: registerPush })
        : await disablePushOnCurrentDevice({ http, unregister: unregisterPush })
      if (result.ok) {
        setPushEnabled(enabled)
      } else {
        // Keep the toggle where it actually is (the attempt did not take) and
        // surface a specific reason instead of a silent no-op.
        setPushEnabled(!enabled)
        setPushError(result.reason)
      }
    } finally {
      setPushBusy(false)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
    } catch (_error) {
      // The server could not be told (offline, 5xx). The local session is cleared by the client
      // either way, so the user still leaves instead of staring at an unchanged panel.
    }
    onClose()
    navigate('/login')
  }

  const handleDownloadData = async () => {
    if (!user?.id || exportBusy) return
    setExportBusy(true)
    setExportError(false)
    try {
      const res = await http.get<Blob>(`/users/${encodeURIComponent(user.id)}/data-export`, {
        responseType: 'blob',
      })
      saveBlob(res.data, dataExportFileName(new Date()))
    } catch (err) {
      console.error('Failed to download data export', err)
      setExportError(true)
    } finally {
      setExportBusy(false)
    }
  }

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault()
    setDeleteLoading(true)
    setDeleteError('')
    try {
      await http.delete(
        `/users/${user?.id}`,
        // `silentErrors`: the dialog shows its own sentence, so the global
        // toast would show a second, different one.
        isOAuthAccount ? silentErrors() : { data: { password: deletePassword }, ...silentErrors() },
      )
      await logout()
      onClose()
      navigate('/login')
    } catch (err) {
      setDeleteError(deleteAccountErrorMessage(err, t))
    } finally {
      setDeleteLoading(false)
    }
  }

  return (
    <div className={cm.cn(cm.sp('p', 4), cm.stack(8))}>
      {/* #4 preview — anchor chips. Expand + reveal a section without
          scrolling for it. Gated behind the mol:settings-nav flag while the
          collapsible layout (the default) is evaluated against it. */}
      {navEnabled && (
        <nav
          data-mol-id="settings-nav"
          aria-label={t('settings.nav.label', undefined, { defaultValue: 'Panel sections' })}
          style={{
            position: 'sticky',
            top: -16,
            zIndex: 1,
            margin: '-16px -16px 0',
            padding: '12px 16px',
            background: 'var(--color-surface)',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <Flex gap="xs" style={{ overflowX: 'auto' }}>
            {SECTIONS.map((id) => (
              // mol-bespoke-button: anchor chip — a 999px pill in a scrolling
              // strip, a shape the button CVA does not ship. Chrome + hover
              // live in .mol-nav-chip (index.css).
              <button
                key={id}
                type="button"
                className="mol-nav-chip"
                data-mol-id={`settings-nav-${id}`}
                onClick={() => expandAndReveal(id)}
              >
                {t(`settings.nav.${id}`, undefined, {
                  defaultValue: SECTION_LABELS[id],
                })}
              </button>
            ))}
          </Flex>
        </nav>
      )}

      {/* Profile — avatar + name + bio, persisted via PATCH /users/:id. Rendered
          by the shared <ProfileEditor> (the SAME form the reusable ProfileModal
          uses) so there's one source of truth for profile editing. The avatar
          renders next to the user's own messages in the Synthase chat (Workspace
          threads user.avatar into <ChatPanel userAvatar=…>). */}
      <div
        ref={(el) => {
          sectionRefs.current.profile = el
        }}
      >
        <SectionCard
          title={t('settings.profile.heading', undefined, { defaultValue: 'Profile' })}
          action={profileSaved ? <SavedIndicator /> : undefined}
        >
          <ProfileEditor onSavedChange={setProfileSaved} />
        </SectionCard>
      </div>

      {/* Account */}
      <div
        ref={(el) => {
          sectionRefs.current.account = el
        }}
      >
        <SectionCard
          title={t('settings.account')}
          action={emailSaving ? <MoleculeSpinner size="xs" /> : undefined}
        >
          <div>
            <Flex align="center" justify="between" className={cm.sp('mb', 1)}>
              <label
                htmlFor="settings-email"
                className={cm.formLabelSmall}
                style={{ marginBottom: 0 }}
              >
                {t('settings.email')}
              </label>
              {/* Verification badge — molecule-dev's truth is emailConfirmedAt. */}
              {userEmail &&
                (emailVerified ? (
                  <span
                    data-mol-id="settings-email-verified"
                    className={cm.cn(cm.textSize('xs'), cm.textSuccess)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    <Icon name="check" size={14} aria-hidden="true" />
                    {t('settings.emailVerified', undefined, { defaultValue: 'Verified' })}
                  </span>
                ) : (
                  <span
                    data-mol-id="settings-email-unverified"
                    className={cm.cn(cm.textSize('xs'), cm.textWarning)}
                  >
                    {t('settings.emailUnverified', undefined, { defaultValue: 'Unverified' })}
                  </span>
                ))}
            </Flex>
            <Input
              id="settings-email"
              type="email"
              value={email}
              onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
              size="sm"
              // Left-aligned like every labeled form field in this panel (the
              // bond's input default is the centered floating-label style).
              align="left"
              // ≥16px on phones so iOS Safari doesn't zoom the page on focus
              // (size="sm" resolves to 15px); desktop keeps the compact size.
              style={isMobile ? { fontSize: 16 } : undefined}
            />
            {/* Explicit update — never saved on blur. Appears only when edited. */}
            {emailDirty && (
              // fullWidth on both: they flex-share the row 50/50, matching the
              // panel's other full-width action buttons.
              <Flex align="center" gap="sm" className={cm.sp('mt', 2)}>
                <Button
                  size="sm"
                  fullWidth
                  data-mol-id="settings-email-update"
                  loading={emailSaving}
                  onClick={handleUpdateEmailClick}
                >
                  {t('settings.updateEmail', undefined, { defaultValue: 'Update email' })}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  fullWidth
                  data-mol-id="settings-email-cancel"
                  onClick={() => {
                    setEmail(userEmail)
                    setEmailError('')
                  }}
                >
                  {t('common.cancel', undefined, { defaultValue: 'Cancel' })}
                </Button>
              </Flex>
            )}
            {emailError && (
              <p className={cm.cn(cm.textSize('xs'), cm.textError, cm.sp('mt', 1))}>{emailError}</p>
            )}
            {emailChangedTo && (
              <p
                className={cm.cn(cm.textSize('xs'), cm.textSuccess, cm.sp('mt', 1))}
                data-mol-id="settings-email-changed-notice"
              >
                {t(
                  'settings.emailChangeSent',
                  { email: emailChangedTo },
                  { defaultValue: `Verification email sent to ${emailChangedTo}.` },
                )}
              </p>
            )}
            {/* Resend — the way out of the unverified state (expired/lost link). */}
            {userEmail && !emailVerified && !emailDirty && !emailChangedTo && (
              <div className={cm.sp('mt', 2)}>
                <Button
                  size="sm"
                  variant="outline"
                  fullWidth
                  data-mol-id="settings-email-resend"
                  disabled={resendState === 'sending' || resendState === 'sent'}
                  onClick={() => void handleResendVerification()}
                >
                  {resendState === 'sent'
                    ? t('settings.verificationSent', undefined, { defaultValue: 'Sent' })
                    : t('settings.resendVerification', undefined, {
                        defaultValue: 'Resend verification email',
                      })}
                </Button>
                {resendState === 'throttled' && (
                  <p className={cm.cn(cm.textSize('xs'), cm.textMuted, cm.sp('mt', 1))}>
                    {t('settings.resendThrottled', undefined, {
                      defaultValue: 'Just sent — try again in a minute.',
                    })}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Password — hidden for OAuth users. (No 2FA toggle: the core
            deliberately DROPS twoFactorEnabled on the generic PATCH — all 2FA
            transitions must go through the TOTP step-up handler — so a toggle
            here would silently no-op. It returns with a real TOTP enrollment
            flow.) */}
          {!(user as unknown as Record<string, unknown>)?.oauthServer && (
            <div style={{ marginTop: 16 }}>
              <Button
                variant="outline"
                size="sm"
                fullWidth
                onClick={() => setShowPasswordModal(true)}
              >
                {t('settings.changePassword')}
              </Button>
            </div>
          )}

          {/* Push notifications */}
          <div style={{ marginTop: 16 }}>
            <Flex align="center" justify="between">
              <span className={cm.textSize('sm')}>{t('settings.pushNotifications')}</span>
              <Switch
                checked={pushEnabled}
                onChange={(e) => handleTogglePush((e.target as HTMLInputElement).checked)}
                disabled={pushBusy}
                size="sm"
                data-mol-id="settings-push-toggle"
              />
            </Flex>
            {pushError && (
              <p className={cm.cn(cm.textSize('xs'), cm.textError, cm.sp('mt', 1))}>
                {pushError === 'permission-denied'
                  ? t('settings.pushBlocked', undefined, {
                      defaultValue: 'Push is blocked or unsupported in this browser.',
                    })
                  : pushError === 'server-unconfigured'
                    ? t('settings.pushServerUnavailable', undefined, {
                        defaultValue: 'Push notifications are not available right now.',
                      })
                    : t('settings.pushUpdateFailed', undefined, {
                        defaultValue: 'Could not update push notifications. Try again.',
                      })}
              </p>
            )}
          </div>
        </SectionCard>
      </div>

      {/* AI — account-level model defaults + bring-your-own providers. Apply to
          projects the user owns; project-level settings/providers take
          precedence. Same planKey→isPro derivation the Workspace uses for its
          ChatPanel/SettingsModal props. */}
      <div
        ref={(el) => {
          sectionRefs.current.ai = el
        }}
      >
        <SectionCard
          molId="settings-section-ai"
          title={t('settings.ai.heading', undefined, { defaultValue: 'AI' })}
          collapsible
          summary={t('settings.ai.summary', undefined, {
            defaultValue: 'Model defaults, your own providers & agent permissions',
          })}
          open={openSections.ai}
          onToggle={(next) => setSectionOpen('ai', next)}
        >
          {everOpened.ai ? (
            <>
              <AccountAiSection
                isPro={
                  !!(user as { planKey?: string } | null)?.planKey?.startsWith('stripe') ||
                  !!(user as { planKey?: string } | null)?.planKey?.startsWith('team')
                }
              />
              {!isGuestUser(user) && <AccountSandboxSection />}
            </>
          ) : null}
        </SectionCard>
      </div>

      {/* Privacy — the opt-in that lets us review this user's Synthase
          sessions (off by default; the section hides itself for a guest). */}
      {!isGuestUser(user) && (
        <div
          ref={(el) => {
            sectionRefs.current.privacy = el
          }}
        >
          <SectionCard
            molId="settings-section-privacy"
            title={t('settings.privacy.heading', undefined, { defaultValue: 'Privacy' })}
            collapsible
            summary={t('settings.privacy.summary', undefined, {
              defaultValue: 'Session sharing preference',
            })}
            open={openSections.privacy}
            onToggle={(next) => setSectionOpen('privacy', next)}
          >
            {everOpened.privacy ? <AccountSessionSharingSection /> : null}
          </SectionCard>
        </div>
      )}

      {/* Billing — current plan + billing period + extra usage/spend cap
          (the former /billing page's content). The upgrade CTA opens the
          UpgradeModal IN PLACE over the panel — never a navigation (the
          workspace's click interceptor isn't mounted on dashboard-side
          pages, so an anchor left the app). The CTA is PINNED under the plan
          line — not header-right, and visible whether the card is collapsed
          or expanded — and it is the panel's ONE upgrade button (the Team
          card below deliberately carries none). */}
      <div
        ref={(el) => {
          sectionRefs.current.billing = el
        }}
      >
        <SectionCard
          molId="settings-section-billing"
          title={t('settings.billing')}
          collapsible
          summary={t('settings.planSummary', { plan }, { defaultValue: '{{plan}} plan' })}
          open={openSections.billing}
          onToggle={(next) => setSectionOpen('billing', next)}
          pinned={
            <Button
              size="sm"
              data-mol-id="settings-upgrade"
              onClick={() => setUpgradeView('pricing')}
            >
              {isPaidPlan
                ? t('settings.changePlan', undefined, { defaultValue: 'Change plan' })
                : t('settings.upgradePlan', undefined, { defaultValue: 'Upgrade plan' })}
            </Button>
          }
        >
          {everOpened.billing && usage ? (
            <>
              <Flex align="center" justify="between" gap="sm" style={{ flexWrap: 'wrap' }}>
                <PlanSummary usage={usage} />
                <ManageBillingButton usage={usage} />
              </Flex>
              <div
                style={{
                  borderTop: '1px solid var(--color-border)',
                  marginTop: 16,
                  paddingTop: 16,
                }}
              >
                <ExtraUsageControls usage={usage} reload={reloadUsage} />
              </div>
              {/* The account-wide badge default (AccountBadgeSection). A free
                account gets an upgrade button (the modal opens in place via
                upgradeView) instead of a toggle the deploy rule would ignore. */}
              <div
                style={{
                  borderTop: '1px solid var(--color-border)',
                  marginTop: 16,
                  paddingTop: 16,
                }}
              >
                <AccountBadgeSection
                  isPaidPlan={isPaidPlan}
                  onUpgrade={() => setUpgradeView('pricing')}
                />
              </div>
            </>
          ) : everOpened.billing ? (
            <span className={cm.cn(cm.textSize('sm'), cm.textMuted)}>
              {t('settings.plan')}
              <strong>{plan}</strong>
            </span>
          ) : null}
        </SectionCard>
      </div>

      {/* Team — the account roster. Lives here rather than in a project's
          workspace so a Team subscriber can put people on their team before
          they have built anything. On a plan without seats the full roster UI
          is a dead end (every control routes to the upgrade modal), so free
          accounts get the note plus an upgrade CTA that is DELIBERATELY
          identical to the Billing card's right above — same label, same
          chrome — so the two read as the same action from two contexts, not
          two different offers. (This also skips the /account/team read that
          plan can never satisfy.) */}
      <div
        ref={(el) => {
          sectionRefs.current.team = el
        }}
      >
        {isPaidPlan ? (
          <SectionCard
            molId="settings-section-team"
            title={t('settings.team', undefined, { defaultValue: 'Team' })}
            collapsible
            summary={t('settings.team.summary', undefined, {
              defaultValue: 'Roster, invitations & seats',
            })}
            open={openSections.team}
            onToggle={(next) => setSectionOpen('team', next)}
          >
            {everOpened.team ? (
              <AccountTeamSection onUpgrade={() => setUpgradeView('pricing')} />
            ) : null}
          </SectionCard>
        ) : (
          <SectionCard title={t('settings.team', undefined, { defaultValue: 'Team' })}>
            {/* Copy as a block, CTA below it — never text beside a control in
                this narrow drawer. */}
            <div className={cm.stack(2)}>
              <span className={cm.cn(cm.textSize('sm'), cm.textMuted)}>
                {t('settings.team.upgradeCopy', undefined, {
                  defaultValue: 'Team rosters, seats and shared billing come with the paid plans.',
                })}
              </span>
              <Button
                size="sm"
                data-mol-id="settings-team-upgrade"
                onClick={() => setUpgradeView('pricing')}
              >
                {t('settings.upgradePlan', undefined, { defaultValue: 'Upgrade plan' })}
              </Button>
            </div>
          </SectionCard>
        )}
      </div>

      {/* Usage — the former /billing page's meters. */}
      <div
        ref={(el) => {
          sectionRefs.current.usage = el
        }}
      >
        <SectionCard
          molId="settings-section-usage"
          title={t('settings.usage', undefined, { defaultValue: 'Usage' })}
          collapsible
          summary={t('settings.usage.summary', undefined, {
            defaultValue: 'Resource, AI & brokered meters',
          })}
          open={openSections.usage}
          onToggle={(next) => setSectionOpen('usage', next)}
          // The spinner only spins for a section that has been ASKED to load —
          // a collapsed card's pending lazy fetch must not read as stuck.
          action={everOpened.usage && usageLoading ? <MoleculeSpinner size="xs" /> : undefined}
        >
          {everOpened.usage &&
            (usageError ? (
              <Flex align="center" gap="sm">
                <span className={cm.cn(cm.textSize('sm'), cm.textError)}>
                  {t('billing.loadError', undefined, {
                    defaultValue: 'Failed to load usage data.',
                  })}
                </span>
                <Button size="sm" variant="outline" onClick={() => void reloadUsage()}>
                  {t('common.retry', undefined, { defaultValue: 'Retry' })}
                </Button>
              </Flex>
            ) : usage ? (
              <>
                <OverLimitBanner usage={usage} />
                <CardSubheading>
                  {t('billing.resources', undefined, { defaultValue: 'Resource usage' })}
                </CardSubheading>
                <ResourceUsageBars usage={usage} />
                {usage.aiMeter && !usage.aiMeter.unmetered && (
                  <div style={{ marginTop: 16 }}>
                    <CardSubheading>
                      {t('billing.aiUsage.title', undefined, { defaultValue: 'AI usage' })}
                    </CardSubheading>
                    <AiUsageMeter usage={usage} />
                  </div>
                )}
                <div style={{ marginTop: 16 }}>
                  <CardSubheading>
                    {t('billing.brokeredUsage', undefined, { defaultValue: 'Brokered usage' })}
                  </CardSubheading>
                  <BrokerUsage usage={usage} />
                </div>
              </>
            ) : null)}
        </SectionCard>
      </div>

      {/* Devices */}
      <div
        ref={(el) => {
          sectionRefs.current.devices = el
        }}
      >
        <SectionCard
          molId="settings-section-devices"
          title={t('settings.devices')}
          collapsible
          summary={
            devicesLoading
              ? t('settings.devices.summary', undefined, {
                  defaultValue: 'Where you are signed in',
                })
              : devices.length === 0
                ? t('settings.devices.none', undefined, { defaultValue: 'No signed-in sessions' })
                : t(
                    'settings.devices.countSummary',
                    { count: devices.length },
                    {
                      defaultValue: '{{count}} signed in',
                    },
                  )
          }
          open={openSections.devices}
          onToggle={(next) => setSectionOpen('devices', next)}
          // Same rule as Usage: no spinner for a lazy fetch that has not been
          // asked to start — the collapsed card would spin forever.
          action={everOpened.devices && devicesLoading ? <MoleculeSpinner size="xs" /> : undefined}
        >
          {everOpened.devices && (
            <>
              {deviceError && (
                <Alert status="error" className={cm.sp('mb', 3)} data-mol-id="devices-error">
                  {deviceError}
                </Alert>
              )}
              {devices.length > 0 ? (
                <ul className={cm.stack(2)}>
                  {devices.map((device) => (
                    <li key={device.id}>
                      <Flex
                        align="center"
                        justify="between"
                        gap="sm"
                        className={cm.cn(cm.textSize('sm'), cm.sp('py', 1))}
                      >
                        <Flex align="center" gap="xs" style={{ minWidth: 0 }}>
                          <span>{device.name || device.platform}</span>
                          {device.current && (
                            <span className={cm.cn(cm.textSize('xs'), cm.textMuted)}>
                              {t('settings.devices.thisOne', undefined, {
                                defaultValue: '(this device)',
                              })}
                            </span>
                          )}
                        </Flex>
                        <Flex align="center" gap="sm">
                          <span className={cm.cn(cm.textSize('xs'), cm.textMuted)}>
                            {device.platform}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            color="error"
                            className={cm.touchTargetCompact}
                            data-mol-id={`device-revoke-${device.id}`}
                            onClick={() => {
                              setDeviceError('')
                              setRevokingDevice(device)
                            }}
                          >
                            {t('settings.devices.signOut', undefined, { defaultValue: 'Sign out' })}
                          </Button>
                        </Flex>
                      </Flex>
                    </li>
                  ))}
                </ul>
              ) : (
                !devicesLoading && (
                  <p className={cm.cn(cm.textSize('sm'), cm.textMuted)}>
                    {t('settings.noDevices')}
                  </p>
                )
              )}

              {/* This device — merged into the Devices card. */}
              <div style={{ marginTop: 16 }}>
                <CardSubheading>{t('settings.thisDevice')}</CardSubheading>
                <dl className={cm.cn(cm.stack(1), cm.textSize('sm'))}>
                  <Flex justify="between">
                    <dt className={cm.textMuted}>{t('settings.platform')}</dt>
                    <dd>{deviceInfo.os.name || t('settings.unknown')}</dd>
                  </Flex>
                  <Flex justify="between">
                    <dt className={cm.textMuted}>{t('settings.browser')}</dt>
                    <dd>{deviceInfo.browser.name || t('settings.unknown')}</dd>
                  </Flex>
                  <Flex justify="between">
                    <dt className={cm.textMuted}>{t('settings.network')}</dt>
                    <dd>{navigator.onLine ? t('settings.online') : t('settings.offline')}</dd>
                  </Flex>
                </dl>
              </div>
            </>
          )}
        </SectionCard>
      </div>

      {/* API tokens — personal tokens for scripts, CI and the CLI. Hidden for
          a guest (no confirmed email; the API refuses them). Content mounts on
          first expansion, and collapsing the card clears any revealed token. */}
      {!isGuestUser(user) && (
        <div
          ref={(el) => {
            sectionRefs.current.apiTokens = el
          }}
        >
          <SectionCard
            molId="settings-section-api-tokens"
            title={t('settings.apiTokens.heading', undefined, { defaultValue: 'API tokens' })}
            collapsible
            summary={t('settings.apiTokens.summary', undefined, {
              defaultValue: 'Tokens for scripts, CI and the CLI',
            })}
            open={openSections.apiTokens}
            onToggle={(next) => setSectionOpen('apiTokens', next)}
          >
            {everOpened.apiTokens ? (
              <AccountApiTokensSection open={openSections.apiTokens} />
            ) : null}
          </SectionCard>
        </div>
      )}

      {/* Log out is the routine action; Danger zone is the destructive one —
          a single red button labeled for the whole zone, opening the delete
          confirmation directly. Visual separation (its own stack below Log
          out) keeps a slide-past misclick from reaching it. */}
      <div className={cm.stack(3)}>
        <Button fullWidth onClick={handleLogout}>
          {t('settings.logOut', undefined, { defaultValue: 'Log out' })}
        </Button>
        {/* Alert does not forward data-* attributes, so the hooks sit on a wrapper. */}
        {exportBusy && (
          <div data-mol-id="settings-data-export-progress">
            <Alert status="info" live={false}>
              {t('settings.dataExport.preparing', undefined, {
                defaultValue: 'Preparing your export.',
              })}
            </Alert>
          </div>
        )}
        {exportError && (
          <div data-mol-id="settings-data-export-error">
            <Alert status="error">
              {t('settings.dataExport.failed', undefined, {
                defaultValue: 'Could not prepare your export. Try again in a minute.',
              })}
            </Alert>
          </div>
        )}
        <Button
          color="secondary"
          fullWidth
          data-mol-id="settings-data-export"
          disabled={exportBusy || !user?.id}
          loading={exportBusy}
          onClick={() => void handleDownloadData()}
        >
          {t('settings.dataExport.button', undefined, { defaultValue: 'Download my data' })}
        </Button>
        <Button
          color="error"
          fullWidth
          data-mol-id="settings-danger"
          onClick={() => setShowDeleteModal(true)}
        >
          {t('settings.danger.heading', undefined, { defaultValue: 'Danger zone' })}
        </Button>
      </div>

      <Footer />

      {/* In-place upgrade flow (pricing view; billing lives in this panel).
          Portals to body above the drawer; closing refreshes usage so a
          completed checkout reflects immediately. */}
      <UpgradeModal
        view={upgradeView}
        onViewChange={setUpgradeView}
        onClose={() => {
          setUpgradeView(null)
          void reloadUsage()
        }}
      />

      {/* Email-change confirmation — the email is the login identifier AND the
          account-recovery anchor, so password accounts re-authenticate before
          the change (the server enforces this; the modal is the UX for it). */}
      <Modal
        open={showEmailConfirmModal}
        onClose={() => {
          setShowEmailConfirmModal(false)
          setEmailConfirmPassword('')
          setEmailError('')
        }}
        title={t('settings.confirmEmailChange.title', undefined, {
          defaultValue: 'Confirm email change',
        })}
        data-mol-id="settings-email-confirm-modal"
      >
        <p className={cm.cn(cm.textSize('sm'), cm.textMuted, cm.sp('mb', 4))}>
          {t(
            'settings.confirmEmailChange.body',
            { email: email.trim() },
            {
              defaultValue: `Enter your password to change your email to ${email.trim()}. A verification link will be sent to the new address.`,
            },
          )}
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void submitEmailChange(emailConfirmPassword)
          }}
          className={cm.stack(4)}
        >
          <div>
            <label htmlFor="email-confirm-pw" className={cm.formLabel}>
              {t('settings.changePasswordModal.currentPassword')}
            </label>
            <Input
              id="email-confirm-pw"
              type="password"
              value={emailConfirmPassword}
              onChange={(e) => setEmailConfirmPassword((e.target as HTMLInputElement).value)}
              autoComplete="current-password"
              autoFocus
              required
              align="left"
              data-mol-id="settings-email-confirm-password"
            />
          </div>
          {emailError && (
            <p className={cm.cn(cm.textSize('xs'), cm.textError)} style={{ margin: 0 }}>
              {emailError}
            </p>
          )}
          <Button
            type="submit"
            fullWidth
            loading={emailSaving}
            data-mol-id="settings-email-confirm-submit"
          >
            {t('settings.updateEmail', undefined, { defaultValue: 'Update email' })}
          </Button>
        </form>
      </Modal>

      {/* Password Change Modal */}
      <Modal
        open={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false)
          resetPw()
        }}
        title={t('settings.changePasswordModal.title')}
      >
        {pwError && (
          <Alert status="error" className={cm.sp('mb', 4)}>
            {pwErrorMessage || t('settings.changePasswordModal.error')}
          </Alert>
        )}
        <form onSubmit={handlePasswordChange} className={cm.stack(4)}>
          <div>
            <label htmlFor="current-pw" className={cm.formLabel}>
              {t('settings.changePasswordModal.currentPassword')}
            </label>
            <Input
              id="current-pw"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword((e.target as HTMLInputElement).value)}
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <label htmlFor="new-pw" className={cm.formLabel}>
              {t('settings.changePasswordModal.newPassword')}
            </label>
            <Input
              id="new-pw"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword((e.target as HTMLInputElement).value)}
              autoComplete="new-password"
              required
            />
          </div>
          <Button type="submit" fullWidth loading={pwStatus === 'pending'}>
            {pwStatus === 'pending'
              ? t('settings.changePasswordModal.changing')
              : t('settings.changePassword')}
          </Button>
        </form>
      </Modal>

      {/* Delete Account Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={t('settings.deleteAccountModal.title')}
      >
        <p className={cm.cn(cm.textSize('sm'), cm.textMuted, cm.sp('mb', 4))}>
          {isOAuthAccount
            ? t('settings.deleteAccountModal.warningOAuth', undefined, {
                defaultValue: DELETE_ACCOUNT_WARNING_OAUTH,
              })
            : t('settings.deleteAccountModal.warning', undefined, {
                defaultValue: DELETE_ACCOUNT_WARNING,
              })}
        </p>
        {deleteError && (
          <Alert status="error" className={cm.sp('mb', 4)}>
            {deleteError}
          </Alert>
        )}
        <form onSubmit={handleDeleteAccount} className={cm.stack(4)}>
          {!isOAuthAccount && (
            <div>
              <label htmlFor="delete-pw" className={cm.formLabel}>
                {t('settings.deleteAccountModal.password')}
              </label>
              <Input
                id="delete-pw"
                type="password"
                autoComplete="current-password"
                value={deletePassword}
                onChange={(e) => setDeletePassword((e.target as HTMLInputElement).value)}
                required
              />
            </div>
          )}
          <Button type="submit" color="error" fullWidth loading={deleteLoading}>
            {deleteLoading
              ? t('settings.deleteAccountModal.deleting')
              : t('settings.deleteAccount')}
          </Button>
        </form>
      </Modal>

      {/* Sign a device out. Confirmed because it is irreversible for that
          session and, when it is THIS device, signs the user out here. */}
      <Modal
        open={Boolean(revokingDevice)}
        onClose={() => setRevokingDevice(null)}
        title={t('settings.devices.signOutModal.title', undefined, {
          defaultValue: 'Sign out this device?',
        })}
      >
        <p className={cm.cn(cm.textSize('sm'), cm.textMuted, cm.sp('mb', 4))}>
          {revokingDevice?.current
            ? t('settings.devices.signOutModal.warningCurrent', undefined, {
                defaultValue: 'This is the device you are using. You will be signed out here.',
              })
            : t(
                'settings.devices.signOutModal.warning',
                { device: revokingDevice?.name || revokingDevice?.platform || '' },
                {
                  defaultValue: '{{device}} will be signed out and will need to log in again.',
                },
              )}
        </p>
        <Button
          color="error"
          fullWidth
          loading={revokeBusy}
          data-mol-id="device-revoke-confirm"
          onClick={() => void confirmRevokeDevice()}
        >
          {t('settings.devices.signOut', undefined, { defaultValue: 'Sign out' })}
        </Button>
      </Modal>
    </div>
  )
}
